import { useEffect, useState } from "react";
import { hasReveal, readDraft } from "@/lib/assessment";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { apiError, track } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";
import { startGoogleLogin } from "@/pages/AuthCallback";
import { toast } from "sonner";

export default function Auth({ mode = "login" }) {
  const isSignup = mode === "signup";
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const saving = hasReveal();
  useEffect(() => {
    if (saving) readDraft().then(({ data }) => setName(data.first_name)).catch(e => setError(apiError(e)));
  }, [saving]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isSignup) {
        const user = await register(name, email, password);
        track("signup_completed", { user_id: user.id });
        navigate(saving ? "/result" : user.onboarded ? (user.assessment_completed ? "/app/home" : "/onboarding") : "/onboarding");
      } else {
        const user = await login(email, password);
        if (saving) navigate("/result");
        else if (["admin", "super_admin"].includes(user.role)) navigate("/admin");
        else if (!user.assessment_completed) navigate(user.onboarded ? "/assessment" : "/onboarding");
        else navigate("/app/home");
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-gradient-to-br from-indigo-600 via-violet-600 to-blue-700 p-12 text-white lg:flex">
        <Logo className="[&_span]:text-white [&_.text-indigo-600]:text-amber-300" />
        <div>
          <h2 className="font-display text-4xl font-extrabold leading-tight">
            {t("heroTitle1")}<br />{t("heroTitle2")}
          </h2>
          <p className="mt-4 max-w-sm text-white/70">
            {t("authSideSub")}
          </p>
        </div>
        <p className="text-xs text-white/50">© {new Date().getFullYear()} NxT · CareerXAI</p>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <div className="mb-8 flex items-center justify-between"><span className="lg:hidden"><Logo /></span><LangToggle className="ml-auto" /></div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight [overflow-wrap:anywhere]" data-testid="auth-heading">
            {saving ? `Save ${name ? name + "'s" : "your"} Career DNA` : isSignup ? t("signupTitle") : t("loginTitle")}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {saving ? "Keep your matches, your personal gift and your next steps together. No card needed." : isSignup ? t("signupSub") : t("loginSub")}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            {isSignup && !saving && (
              <div>
                <label className="text-sm font-semibold">{t("name")}</label>
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  required placeholder={t("namePh")}
                  className="mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
              </div>
            )}
            <div>
              <label className="text-sm font-semibold">{t("email")}</label>
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required placeholder="you@email.com"
                className="mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold">{t("password")}</label>
                {!isSignup && <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-bold text-indigo-600 hover:underline">{t("forgotPassword")}</Link>}
              </div>
              <input data-testid="auth-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                required minLength={6} placeholder="••••••••"
                className="mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm font-medium text-destructive">{error}</p>}
            <button type="submit" disabled={loading || (saving && !name)} data-testid="auth-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold text-white transition-all hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{saving ? "Save my Career DNA" : isSignup ? t("createAccount") : t("login")} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("or")}<span className="h-px flex-1 bg-border" /></div>
          <button type="button" onClick={startGoogleLogin} data-testid="google-login-btn"
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-border bg-card py-3.5 font-bold transition-colors hover:bg-secondary">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.8 6.1C12.3 13.6 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4H24v7.6h12.9c-.3 2.1-1.7 5.3-4.9 7.4l7.5 5.8c4.5-4.1 7-10.2 7-16.8z"/><path fill="#FBBC05" d="M10.4 28.6A14.6 14.6 0 0 1 9.5 24c0-1.6.3-3.2.8-4.6l-7.8-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.7l7.8-6.1z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2 1.4-4.7 2.4-8.4 2.4-6.3 0-11.7-4.1-13.6-9.9l-7.8 6.1C6.5 42.6 14.6 48 24 48z"/></svg>
            {t("continueGoogle")}
          </button>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {isSignup ? t("haveAccount") : t("newHere")}
            <Link to={isSignup ? "/login" : "/signup"} data-testid="auth-switch-link"
              className="font-bold text-indigo-600 hover:underline">
              {isSignup ? t("login") : t("createOne")}
            </Link>
          </p>
          <p className="mt-4 text-center text-[11px] text-muted-foreground">{t("agree")} <Link to="/terms" className="underline">{t("terms")}</Link> · <Link to="/privacy" className="underline">{t("privacy")}</Link></p>
        </motion.div>
      </div>
    </div>
  );
}
